<template>
  <o-dialog
    ref="dialog"
    :title="$t('giftCard.new')"
    :info="info"
    :options="options"
    :loading="loading"
    :portal="portal"
  >
    <div class="ext-left">
      <o-form v-if="giftCard" ref="form">
        <div>
          <o-field-group
            v-slot="{ status }"
            class="mb-4 block"
            rules="required"
          >
            <segment-field v-model="type" :options="giftCardTypeOptions" :status="status" />
          </o-field-group>

          <div v-if="isAssign" class="border rounded-lg p-3 bg-gray-50 mb-3 dark:bg-gray-900">
            <o-field-group
              v-slot="{ status }"
              :label="$t('general.customer')"
              :name="$t('general.customer')"
              class="block"
              rules="required"
            >
              <customer-select v-model="giftCard.customer" preview create default-status-class="bg-white h-10 dark:bg-gray-900" :status="status" />
            </o-field-group>
          </div>

          <div v-if="isGift" class="border rounded-lg p-3 bg-gray-50 mb-3 dark:bg-gray-900">
            <o-field-group
              v-slot="{ status }"
              :label="$t('general.senderName')"
              :name="$t('general.senderName')"
              class="mb-4 block"
              rules="required"
            >
              <text-field v-model="giftCard.gift.from.name" :status="status" />
            </o-field-group>


            <o-field-group
              v-slot="{ status }"
              :label="$t('general.recipientName')"
              :name="$t('general.recipientName')"
              class="mb-4 block"
              rules="required"
            >
              <text-field v-model="giftCard.gift.to.name" :status="status" />
            </o-field-group>


            <o-field-group
              v-slot="{ status }"
              :label="$t('general.recipientEmail')"
              :name="$t('general.recipientEmail')"
              class="mb-4 block"
              rules="required|email"
            >
              <text-field v-model="giftCard.gift.to.email" :status="status" />
            </o-field-group>


            <o-field-group
              v-slot="{ status }"
              :label="$t('general.message')"
              :name="$t('general.message')"
              class="mb-4 block"
              rules="required"
            >
              <text-area-field v-model="giftCard.gift.message" :status="status" />
            </o-field-group>

            <o-field-group
              v-if="giftCardImageOptions.length > 1"
              :label="$t('pages.minisite.index.giftCards.giftCardImage')"
              class="mb-4 block"
            >
              <div class="flex flex-wrap gap-3">
                <button
                  v-for="(image, index) in giftCardImageOptions"
                  :key="image.idGiftCardImage ?? 'default'"
                  type="button"
                  data-testid="gift-card-image-option"
                  class="relative h-24 w-32 overflow-hidden rounded-lg border-2 bg-white transition hover:border-primary-400 dark:bg-gray-800"
                  :class="giftCard.idGiftCardImage === image.idGiftCardImage
                    ? 'border-primary-500 ring-2 ring-primary-500'
                    : 'border-gray-200 dark:border-gray-700'"
                  :aria-label="image.isDefault
                    ? $t('general.default')
                    : `${$t('pages.minisite.index.giftCards.giftCardImage')} ${index + 1}`"
                  :aria-pressed="giftCard.idGiftCardImage === image.idGiftCardImage"
                  @click="giftCard.idGiftCardImage = image.idGiftCardImage"
                >
                  <img
                    v-if="image.url"
                    :src="image.url"
                    alt=""
                    class="h-full w-full object-cover"
                  >
                  <span
                    v-else
                    class="flex h-full w-full items-center justify-center text-sm text-gray-500 dark:text-gray-300"
                  >
                    {{ $t('general.default') }}
                  </span>
                  <span
                    v-if="image.isDefault && image.url"
                    class="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 py-0.5 text-xs text-white"
                  >
                    {{ $t('general.default') }}
                  </span>
                </button>
              </div>
            </o-field-group>


            <o-field-group
              v-slot="{ status }"
              :label="$t('general.deliveryDate')"
              :name="$t('general.deliveryDate')"
              date
              class="block text-left"
              rules="required"
              :message="deliveryDateLabel"
            >
              <date-field
                v-model="giftCard.gift.deliveryDate"
                :augmenter="Augmenters.timestamp"
                :status="status"
                :placeholder="$t('datetime.labels.today')"
                :min-date="new Date()"
              />
            </o-field-group>
          </div>

          <div class="border rounded-lg p-3 bg-gray-50 dark:bg-gray-900">
            <o-field-group
              v-slot="{ status }"
              :label="$t('general.balance')"
              :name="$t('general.balance')"
              class="mb-4 block"
              rules="required"
            >
              <currency-field v-model="giftCard.balance" :status="status" />
            </o-field-group>

            <o-field-group
              v-slot="{ status }"
              :label="$t('giftCard.customNumber')"
              :name="$t('giftCard.customNumber')"
              class="mb-4 block"
            >
              <text-field v-model="giftCard.customNumber" :status="status" />
            </o-field-group>

            <o-field-group
              :label="$t('general.expiryDate')"
              :name="$t('general.expiryDate')"
              date
              :message="defaultExpiryLabel"
              class="block"
            >
              <date-field
                v-model="giftCard.expiryDate"
                :augmenter="Augmenters.timestamp"
                :placeholder="$t('giftCard.neverExpires')"
                clear
              />
            </o-field-group>
          </div>
        </div>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import CustomerSelect from '@/components/customer/elements/Select.vue'
import {
  TextField,
  CurrencyField,
  DateField,
  SegmentField,
  TextAreaField,
} from '@/components/fields'
import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'
import { GiftCard } from '~/_api/models/giftCard'

const { $api, $notification, $bus, $auth, $date, $panel, $i18n } = useNuxtApp()

interface Props {
  portal?: string
  loading?: boolean
  search?: boolean
}

interface GiftCardImageOption {
  idGiftCardImage: number | null
  isDefault: boolean
  url: string | null
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
  search: false,
})

const emit = defineEmits<{
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'update:order', order: any): void
  (e: 'search'): void
}>()

const dialog = ref<any>()
const form = ref<any>()

const giftCard = ref<any>(new GiftCard())
const giftCardImages = ref<any>(null)
const giftCardImagesLoading = ref(false)
const order = ref<any>(null)
const type = ref('gift')
const giftCardTypeOptions = computed(() => [
  { label: $i18n.t('general.gift'), value: 'gift' },
  { label: $i18n.t('general.assign'), value: 'assign' },
])

const info = computed(() => {
  return type.value === 'gift' ? $i18n.t('components.giftCard.actions.add.wontLink') : $i18n.t('components.giftCard.actions.add.willLink')
})

const options = computed(() => {
  const opts: any[] = [
    { text: $i18n.t('general.cancel'), type: 'cancel' },
    { text: $i18n.t('general.save'), type: 'confirm', variant: 'primary', callback: confirm },
  ]

  if (props.search) {
    opts.push({ text: $i18n.t('components.giftCard.actions.add.search'), type: 'confirm', variant: 'primary', outline: true, callback: handleSearch })
  }

  return opts
})

const deliveryDateLabel = computed(() => {
  if (giftCard.value.gift?.deliveryDate) {
    return $i18n.t('components.giftCard.actions.add.deliveryDateLabelDate', { date: $date.format(giftCard.value.gift.deliveryDate * 1000, $date.presets.long) })
  }

  return $i18n.t('components.giftCard.actions.add.deliveryDateLabelImmediate')
})

const defaultExpiryLabel = computed(() => {
  const expiry = $auth.info.location.giftCardExpiryDays

  if (expiry) {
    return $i18n.t('components.giftCard.actions.add.defaultExpiryLabelExpires', { expiry })
  }

  return $i18n.t('components.giftCard.actions.add.defaultExpiryLabelDoesntExpire')
})

const isGift = computed(() => {
  return type.value === 'gift'
})

const isAssign = computed(() => {
  return type.value === 'assign'
})

const giftCardImageOptions = computed<GiftCardImageOption[]>(() => {
  const defaultImage = giftCardImages.value?.giftCardImage
  const options: GiftCardImageOption[] = [{
    idGiftCardImage: null,
    isDefault: true,
    url: defaultImage?.hasGiftCardImage
      ? defaultImage.imageUrls?.medium ?? defaultImage.imageUrls?.large ?? null
      : null,
  }]

  for (const image of giftCardImages.value?.additionalGiftCardImages ?? []) {
    const idGiftCardImage = image.idGiftCardImage ?? image.id
    const url = image.imageUrls?.medium ?? image.imageUrls?.large ?? null

    if (idGiftCardImage == null || !url) continue

    options.push({
      idGiftCardImage: Number(idGiftCardImage),
      isDefault: false,
      url,
    })
  }

  return options
})

async function fetchGiftCardImages () {
  if (giftCardImages.value || giftCardImagesLoading.value) return

  giftCardImagesLoading.value = true

  try {
    giftCardImages.value = (await $api.minisite.getImage('miniSiteGiftCardImage')).data
  } catch {
    // Image selection is optional; gift card creation must remain available to
    // employees who cannot read minisite settings.
  } finally {
    giftCardImagesLoading.value = false
  }
}

function open ({ customer = null, balance = null, customNumber = '', order: orderData }: any = {}) {
  const gc = new GiftCard()

  const giftCardExpiryDays = $auth.info.location.giftCardExpiryDays
  const expiryDate = giftCardExpiryDays ? $date.timestamp($date.add(new Date(), { days: giftCardExpiryDays })) : null

  gc.customer = customer
  gc.balance = balance
  gc.customNumber = customNumber
  gc.expiryDate = expiryDate
  gc.idGiftCardImage = null
  gc.gift = {
    to: {
      name: '',
      email: '',
    },
    from: {
      name: '',
    },
    message: $i18n.t('components.giftCard.actions.add.message'),
    deliveryDate: null,
  }

  giftCard.value = gc
  order.value = orderData

  dialog.value.open()
  void fetchGiftCardImages()
}

async function confirm () {
  emit('update:loading', true)

  try {
    if (isGift.value) {
      giftCard.value.customer = null
    }

    if (isAssign.value) {
      giftCard.value.gift = null
      giftCard.value.idGiftCardImage = null
    }

    if (isAssign.value && giftCard.value.customer.isNewRecord) {
      const customer = await saveCustomer()

      if (!customer) {
        emit('update:loading', false)

        return
      }
    }

    const gcResponse = await $api.giftCard.save(giftCard.value)
    const gc = gcResponse.data

    if (gc) {
      emit('success')

      $notification.toast({
        title: $i18n.t('components.giftCard.actions.add.createdSuccess'),
        text: $i18n.t('components.giftCard.actions.add.createdSuccessInfo'),
        variant: 'success',
      })

      $bus.emit.created('GiftCard', gc)

      if (order.value) {
        const orderResponse = await $api.order.addGiftCards(
          String(order.value.idOrder),
          [String(gc.objectID())],
        )

        if (orderResponse.data) {
          emit('update:order', orderResponse.data)
        }
      } else {
        const path = `/customer/giftCard/${gc.idGiftCard}`
        $panel.create(path)
      }

      emit('update:loading', false)

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  emit('update:loading', false)
}

async function saveCustomer () {
  try {
    const customer = (await $api.customer.save(giftCard.value.customer)).data

    if (customer) {
      $notification.toast({
        title: $i18n.t('customer.createdSuccess'),
        text: $i18n.t('customer.createdSuccessInfo', { name: customer.displayName }),
        variant: 'success',
      })

      $bus.emit.created('Customer', customer)

      return true
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }
}

function handleSearch () {
  emit('search')
  return true
}

defineExpose({ open })
</script>
